<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import mapBackground from '../assets/design/backgrounds/map-background.png'
import { getMemoryIcon } from '../assets/design/memory-icons'
import { getTasteIcon } from '../assets/design/taste-icons'
import {
  getMapRelations,
  getMemoryMapNodes,
  getWordStats,
} from '../services/experience-service'
import { useExperienceStore } from '../stores/experience'
import type { MemoryMapNode, RelationStat, WordStat } from '../types/questionnaire'

interface BubbleSlot {
  x: number
  y: number
}

interface TasteBubble extends WordStat, BubbleSlot {
  size: number
  color: string
  icon?: string
}

interface MemoryBubble extends MemoryMapNode, BubbleSlot {
  size: number
  color: string
  icon?: string
}

interface RelationLine {
  sourceCode: string
  targetCode: string
  count: number
  path: string
  strokeWidth: number
  opacity: number
  color: string
  active: boolean
}

const tasteSlots: BubbleSlot[] = [
  { x: 8, y: 26 },
  { x: 21, y: 19 },
  { x: 35, y: 26 },
  { x: 13, y: 48 },
  { x: 27, y: 48 },
  { x: 40, y: 47 },
  { x: 7, y: 67 },
  { x: 19.5, y: 69 },
  { x: 32.5, y: 68 },
  { x: 41, y: 67 },
]

const memorySlots: BubbleSlot[] = [
  { x: 61, y: 19 },
  { x: 76, y: 17 },
  { x: 91, y: 24 },
  { x: 60, y: 51 },
  { x: 73, y: 50 },
  { x: 86, y: 50 },
  { x: 96, y: 54 },
  { x: 62, y: 75 },
  { x: 77, y: 75 },
  { x: 91, y: 75 },
]

const MIN_BUBBLE_SIZE = 6.5
const MAX_BUBBLE_SIZE = 10.5

const colors = [
  '#ff9e87',
  '#f48ba1',
  '#ffd358',
  '#cdd275',
  '#abc5e7',
  '#9bb49c',
  '#e9bdd0',
  '#b3c1e6',
  '#a9d7d9',
  '#edc58d',
]

const router = useRouter()
const experienceStore = useExperienceStore()
const tasteStats = ref<WordStat[]>([])
const memoryNodes = ref<MemoryMapNode[]>([])
const mapRelations = ref<RelationStat[]>([])
const loading = ref(true)
const errorMessage = ref('')
const selectedTasteCode = ref('')

function scaleBubbleSize(count: number, counts: number[]) {
  const minCount = Math.min(...counts)
  const maxCount = Math.max(...counts)
  if (minCount === maxCount) {
    return (MIN_BUBBLE_SIZE + MAX_BUBBLE_SIZE) / 2
  }

  const minRoot = Math.sqrt(minCount)
  const range = Math.sqrt(maxCount) - minRoot
  const ratio = (Math.sqrt(count) - minRoot) / range
  return MIN_BUBBLE_SIZE + ratio * (MAX_BUBBLE_SIZE - MIN_BUBBLE_SIZE)
}

function createTasteBubbles(stats: WordStat[]): TasteBubble[] {
  const source = [...stats]
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, tasteSlots.length)

  if (source.length === 0) {
    return []
  }

  const counts = source.map((item) => item.count)
  return source.map((item, index) => {
    const slot = tasteSlots[index]
    return {
      ...item,
      ...slot,
      size: scaleBubbleSize(item.count, counts),
      color: colors[index % colors.length],
      icon: getTasteIcon(item.code),
    }
  })
}

const tasteBubbles = computed(() => createTasteBubbles(tasteStats.value))
const memoryBubbles = computed<MemoryBubble[]>(() => {
  const source = [...memoryNodes.value]
    .filter((item) => item.count > 0)
    .sort((left, right) => right.count - left.count)
    .slice(0, memorySlots.length)

  if (source.length === 0) {
    return []
  }

  const counts = source.map((item) => item.count)
  return source.map((item, index) => {
    const slot = memorySlots[index]
    return {
      ...item,
      ...slot,
      size: scaleBubbleSize(item.count, counts),
      color: colors[(index + 4) % colors.length],
      icon: getMemoryIcon(item.categoryCode),
    }
  })
})
const hasMapData = computed(() => tasteBubbles.value.length > 0 || memoryBubbles.value.length > 0)
const selectedTaste = computed(() => (
  tasteBubbles.value.find((item) => item.code === selectedTasteCode.value) || null
))
const relatedMemoryCodes = computed(() => new Set(
  mapRelations.value
    .filter((item) => item.sourceCode === selectedTasteCode.value)
    .map((item) => item.targetCode),
))
const mapDescription = computed(() => {
  if (!selectedTaste.value) {
    return '不同味道串起了大家不同的记忆与感受'
  }
  if (relatedMemoryCodes.value.size === 0) {
    return `“${selectedTaste.value.label}”还没有可展示的记忆关联`
  }
  return `“${selectedTaste.value.label}”连接了 ${relatedMemoryCodes.value.size} 类记忆`
})
const relationLines = computed<RelationLine[]>(() => {
  const visibleRelations = mapRelations.value.filter((relation) => (
    tasteBubbles.value.some((item) => item.code === relation.sourceCode)
    && memoryBubbles.value.some((item) => item.categoryCode === relation.targetCode)
  ))
  const maxCount = Math.max(1, ...visibleRelations.map((item) => item.count))
  return visibleRelations.flatMap((relation) => {
    const source = tasteBubbles.value.find((item) => item.code === relation.sourceCode)
    const memory = memoryBubbles.value.find((item) => item.categoryCode === relation.targetCode)
    if (!source || !memory) {
      return []
    }

    const controlOffset = Math.max(10, (memory.x - source.x) * 0.42)
    const strength = Math.sqrt(relation.count / maxCount)
    const active = !selectedTasteCode.value || relation.sourceCode === selectedTasteCode.value
    return [{
      sourceCode: relation.sourceCode,
      targetCode: relation.targetCode,
      count: relation.count,
      path: `M ${source.x} ${source.y} C ${source.x + controlOffset} ${source.y}, ${memory.x - controlOffset} ${memory.y}, ${memory.x} ${memory.y}`,
      strokeWidth: 1.5 + strength * 7.5,
      opacity: selectedTasteCode.value
        ? (active ? 0.48 + strength * 0.34 : 0.07)
        : 0.16 + strength * 0.28,
      color: source.color,
      active,
    }]
  }).sort((left, right) => left.count - right.count)
})

onMounted(async () => {
  try {
    const [taste, memories, relations] = await Promise.all([
      getWordStats('taste'),
      getMemoryMapNodes(10),
      getMapRelations(100),
    ])
    tasteStats.value = taste
    memoryNodes.value = memories
    mapRelations.value = relations
  } catch {
    errorMessage.value = experienceStore.lastSubmissionQueued
      ? '本次回答已离线保存，恢复网络后将自动提交并更新地图'
      : '味觉地图暂时加载失败'
  } finally {
    loading.value = false
  }
})

function returnHome() {
  experienceStore.resetSession()
  void router.replace({ name: 'home' })
}

function clearRelations() {
  selectedTasteCode.value = ''
}

function selectTaste(bubble: TasteBubble) {
  selectedTasteCode.value = selectedTasteCode.value === bubble.code ? '' : bubble.code
}
</script>

<template>
  <div class="view map-view" :style="{ backgroundImage: `url(${mapBackground})` }">
    <header class="map-heading">
      <h1>大家的味觉地图</h1>
      <p>{{ mapDescription }}</p>
    </header>

    <div v-if="loading" class="map-status" aria-live="polite">
      正在汇聚大家的味觉与记忆…
    </div>
    <div v-else-if="errorMessage" class="map-status map-status--error" aria-live="polite">
      {{ errorMessage }}
    </div>
    <div v-else-if="hasMapData" class="bubble-map" aria-live="polite" @click.self="clearRelations">
      <p class="bubble-column-label bubble-column-label--taste">味道</p>
      <p class="bubble-column-label bubble-column-label--memory">记忆</p>
      <svg
        v-if="relationLines.length > 0"
        class="relation-lines"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          v-for="line in relationLines"
          :key="`${line.sourceCode}-${line.targetCode}`"
          class="relation-line"
          :class="{ 'relation-line--muted': !line.active }"
          :d="line.path"
          :stroke="line.color"
          :stroke-width="line.strokeWidth"
          :opacity="line.opacity"
        />
      </svg>
      <button
        v-for="bubble in tasteBubbles"
        :key="`taste-${bubble.code}`"
        class="map-bubble map-bubble--taste"
        :class="{
          'map-bubble--highlighted': bubble.code === experienceStore.lastSubmittedTasteCode,
          'map-bubble--selected': bubble.code === selectedTasteCode,
          'map-bubble--dimmed': selectedTasteCode && bubble.code !== selectedTasteCode,
        }"
        :style="{
          left: `${bubble.x}%`,
          top: `${bubble.y}%`,
          width: `${bubble.size}%`,
          paddingTop: `${bubble.size}%`,
        }"
        type="button"
        :aria-pressed="bubble.code === selectedTasteCode"
        :title="`${bubble.label}：${bubble.count} 次`"
        @click="selectTaste(bubble)"
      >
        <span class="map-bubble-visual" :style="{ backgroundColor: bubble.color }">
          <img v-if="bubble.icon" class="map-bubble-icon" :src="bubble.icon" alt="">
          <span v-else class="map-bubble-fallback">{{ bubble.label }}</span>
        </span>
        <span class="map-bubble-caption map-bubble-caption--taste">
          <strong>{{ bubble.label }}</strong>
        </span>
      </button>
      <article
        v-for="bubble in memoryBubbles"
        :key="`memory-${bubble.categoryCode}`"
        class="map-bubble map-bubble--memory"
        :class="{
          'map-bubble--highlighted': bubble.categoryCode === experienceStore.lastSubmittedMemoryCode,
          'map-bubble--related': relatedMemoryCodes.has(bubble.categoryCode),
          'map-bubble--dimmed': selectedTasteCode && !relatedMemoryCodes.has(bubble.categoryCode),
        }"
        :style="{
          left: `${bubble.x}%`,
          top: `${bubble.y}%`,
          width: `${bubble.size}%`,
          paddingTop: `${bubble.size}%`,
        }"
        :title="bubble.memoryText ? `${bubble.categoryLabel}：${bubble.memoryText}` : bubble.categoryLabel"
      >
        <span class="map-bubble-visual" :style="{ backgroundColor: bubble.color }">
          <img v-if="bubble.icon" class="map-bubble-icon" :src="bubble.icon" alt="">
          <span v-else class="map-bubble-fallback map-bubble-fallback--memory" aria-hidden="true">✦</span>
        </span>
        <span class="map-bubble-caption map-bubble-caption--memory">
          <strong>{{ bubble.categoryLabel }}</strong>
          <small v-if="bubble.memoryText">“{{ bubble.memoryText }}”</small>
        </span>
      </article>
    </div>
    <div v-else class="map-status" aria-live="polite">
      还没有真实答卷，完成一次体验后这里会出现统计
    </div>

    <p
      v-if="experienceStore.lastSubmissionQueued && !errorMessage"
      class="map-sync-notice"
      aria-live="polite"
    >
      本次回答已离线保存，恢复网络后将自动提交
    </p>

    <button class="map-return" type="button" @click="returnHome">
      返回首页
    </button>
  </div>
</template>
